import { getAddOnLimitError } from "common";
import { useMemo, useState } from "react";

export interface OverrideCatalogItemRestrictions {
    _overrideCatalogItemRestrictions?: boolean;
}

export interface AddOnLimitOverride {
    /** True while an exceeded limit stands with the override off, so the change cannot be saved. */
    isAddonLimitExceeded: boolean;
    isOverriding: boolean;
    /** The message from the exceeded limit. Hidden while the override is on, and shown again when it is turned off. */
    limitError?: string;
    /** Spread into the item payload to send the override. */
    itemOverride: OverrideCatalogItemRestrictions;
    setIsOverriding: (value: boolean) => void;
    /** Returns true when the failure was an exceeded limit, so the caller can skip its own error handling. */
    handleError: (error: unknown) => boolean;
    /** Clears the message after a request without the override succeeds. A success with the override on proves nothing. */
    clearError: () => void;
    /** Clears the message and the override, for when the selection changes. */
    reset: () => void;
}

/** The client learns about a limit error only from the API, so the toggle shows with the error. */
export const useAddOnLimitOverride = (): AddOnLimitOverride => {
    const [message, setMessage] = useState<string>();
    const [isOverriding, setIsOverriding] = useState(false);
    const limitError = isOverriding ? undefined : message;

    const clearError = () => {
        if (!isOverriding) {
            setMessage(undefined);
        }
    };

    const reset = () => {
        setMessage(undefined);
        setIsOverriding(false);
    };

    const handleError = (error: unknown) => {
        const errorMessage = getAddOnLimitError(error);
        if (errorMessage) {
            setMessage(errorMessage);
        }

        return Boolean(errorMessage);
    };

    // Memoized so callers can use it as a dependency.
    const itemOverride = useMemo(() => {
        return isOverriding ? { _overrideCatalogItemRestrictions: true } : {};
    }, [isOverriding]);

    return {
        isAddonLimitExceeded: Boolean(limitError),
        isOverriding,
        limitError,
        itemOverride,
        setIsOverriding,
        handleError,
        clearError,
        reset,
    };
};
